import React, { useEffect } from 'react'
import { Link, matchPath, useLocation } from 'react-router-dom'
import { AppBar, Badge, Box, Button, IconButton, Tab, Tabs, Toolbar, Tooltip, alpha } from '@mui/material'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined'
import logo from 'src/designSystem/AppBars/logo.svg'
import { FeatureFlagMenu } from 'src/featureFlags'
import { useIsAdmin, useIsCommunityFacilitator } from 'src/session/useSession'
import { advancedColors, onDark } from '../theme'
import { useEditMode } from '../dashboard/useEditMode'
import { TOUR_ANCHORS } from '../tour/anchors'
import { useProductUpdates } from '../productUpdates/ProductUpdatesProvider'
import { useAdvancedMode } from './advancedMode'
import AdvancedDeveloperMenu from './AdvancedDeveloperMenu'
import AdvancedModeSwitch from './AdvancedModeSwitch'

export const ADVANCED_APP_BAR_HEIGHT = 56

type TabDefinition = { label: string; to: string }

const getActiveTab = (tabs: TabDefinition[], pathname: string): string | false => {
  const match = tabs.find((tab) => pathname === tab.to || pathname.startsWith(`${tab.to}/`))

  return match ? match.to : false
}

const navItemSx = {
  color: onDark.primary,
  textTransform: 'uppercase' as const,
  textDecoration: 'none',
  px: 1,
  borderRadius: 1,
  '&:hover': { backgroundColor: alpha(onDark.primary, 0.08), textDecoration: 'none' },
}

type Props = { onStartTour: () => void }

const AdvancedAppBar = ({ onStartTour }: Props) => {
  const location = useLocation()
  const isAdmin = useIsAdmin()
  const isCommunityFacilitator = useIsCommunityFacilitator()
  const [editMode, toggleEditMode] = useEditMode()
  const [, setAdvancedMode] = useAdvancedMode()
  const productUpdates = useProductUpdates()

  useEffect(() => {
    setAdvancedMode(true)
  }, [setAdvancedMode])

  const tabs: TabDefinition[] = [
    { label: 'Home', to: '/advanced' },
    { label: 'Participants', to: '/participants' },
  ]
  if (isAdmin) {
    tabs.push({ label: 'Admin', to: '/admin' })
  }
  if (isAdmin || isCommunityFacilitator) {
    tabs.push({ label: 'Communities', to: '/communities' })
  }
  if (isAdmin) {
    tabs.push({ label: 'Advanced config', to: '/advanced-config' })
  }

  const activeTab = getActiveTab(tabs, location.pathname)
  const patientOpen = matchPath(location.pathname, { path: '/advanced/:uid' }) !== null

  return (
    <AppBar
      position="sticky"
      elevation={2}
      sx={{
        height: ADVANCED_APP_BAR_HEIGHT,
        backgroundColor: advancedColors.deep,
        backgroundImage: 'none',
        border: 'none',
        borderRadius: 0,
        boxShadow: (theme) => theme.shadows[2],
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar variant="dense" sx={{ minHeight: ADVANCED_APP_BAR_HEIGHT }}>
        <Link to="/advanced" aria-label="Main dashboard" style={{ textDecoration: 'none' }}>
          <Box
            component="img"
            alt="Meru Health"
            src={logo}
            width={25}
            height={25}
            sx={{ display: 'block', m: 2, filter: 'brightness(0) invert(1)' }}
          />
        </Link>

        <Box sx={{ display: 'flex', flex: 1, mx: 3 }}>
          <Tabs
            value={activeTab}
            textColor="inherit"
            TabIndicatorProps={{ style: { backgroundColor: onDark.accentGreen, height: 3 } }}
            sx={{
              minHeight: ADVANCED_APP_BAR_HEIGHT,
              '& .MuiTab-root': {
                color: onDark.secondary,
                minHeight: ADVANCED_APP_BAR_HEIGHT,
                textDecoration: 'none',
                backgroundColor: 'transparent',
                '&:hover': { textDecoration: 'none', backgroundColor: alpha(onDark.primary, 0.08) },
              },
              '& .MuiTab-root.Mui-selected': {
                color: onDark.primary,
                backgroundColor: 'transparent',
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.to} component={Link} to={tab.to} label={tab.label} value={tab.to} />
            ))}
          </Tabs>
        </Box>

        <FeatureFlagMenu buttonSx={navItemSx} />
        {isAdmin && <AdvancedDeveloperMenu />}

        <Tooltip title="Product updates">
          <IconButton
            data-tour={TOUR_ANCHORS.productUpdatesButton}
            aria-label={
              productUpdates.unseen.length > 0
                ? `Product updates, ${productUpdates.unseen.length} unread`
                : 'Product updates'
            }
            onClick={productUpdates.open}
            sx={{ color: onDark.primary, '&:hover': { backgroundColor: alpha(onDark.primary, 0.08) } }}
          >
            <Badge badgeContent={productUpdates.unseen.length} color="error">
              <NewReleasesOutlinedIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>

        {patientOpen && (
          <Tooltip title="Take the tour">
            <IconButton
              aria-label="Take the tour"
              onClick={onStartTour}
              sx={{ color: onDark.primary, '&:hover': { backgroundColor: alpha(onDark.primary, 0.08) } }}
            >
              <HelpOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={editMode ? 'Switch to clinician mode' : 'Switch to edit mode'}>
          <Button
            onClick={toggleEditMode}
            sx={{ ...navItemSx, color: editMode ? onDark.accentGreen : onDark.secondary }}
          >
            {editMode ? 'Edit mode' : 'Clinician mode'}
          </Button>
        </Tooltip>

        <AdvancedModeSwitch />

        <Button component={Link} to="/logout" sx={navItemSx}>
          Logout
        </Button>
      </Toolbar>
    </AppBar>
  )
}

export default AdvancedAppBar
